import { yupResolver } from '@hookform/resolvers/yup';
import { useCreateOtherIncome } from 'api-hooks/other-income';
import { SaleModel } from 'api-hooks/sale/common';
import { TourModel } from 'api-hooks/tour/tour-list/tour-detail';
import formSetErrors from 'common/helpers/form-setError';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody } from 'components/common/drawer/styles';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form, Input } from 'components/elements';
import Button from 'components/elements/buttons/button';
import useDismiss from 'hooks/use-dismiss';
import { FullContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useForm } from 'react-hook-form';

import {
  OtherIncomeCreateFormSchema,
  OtherIncomeCreateFormType,
  OtherIncomeDetailCreateFormType,
} from './components/form-type';
import OtherIncomeCard from './components/other-income-card';

interface OtherIncomeCreateProps {
  incomableRefType: 'Tour' | 'Sale';
  data: TourModel | SaleModel;
  onClose: () => void;
}

export default function OtherIncomeCreate(props: OtherIncomeCreateProps) {
  const { incomableRefType, data } = props;
  const onDismiss = useDismiss('other-incomes');
  const { mutateAsync } = useCreateOtherIncome();
  const { t } = useTranslation();

  const defaultValues = React.useMemo<OtherIncomeCreateFormType>(() => {
    return {
      incomableRefId: data.refId,
      incomableRefType,
      otherIncomes: [
        {
          accountableId: null,
          accountType: null,
          acknowledgedAt: null,
          amount: undefined,
          description: '',
          transactionAt: new Date(),
        } as unknown as OtherIncomeDetailCreateFormType,
      ],
    };
  }, [data.refId, incomableRefType]);

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(OtherIncomeCreateFormSchema()),
  });

  const onSubmit = React.useCallback(
    async (values: OtherIncomeCreateFormType) => {
      try {
        const result = await mutateAsync(values);
        notification.success({
          message: result.message,
        });
        onDismiss();
        props.onClose();
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        e.message &&
          notification.error({
            message: e.message,
          });
      }
    },
    [methods.setError, mutateAsync, onDismiss, props],
  );

  return (
    <>
      <DrawerHeader
        onClose={props.onClose}
        title={t('common:add_extra', { extra: t('common:other_incomes') })}
      />
      <DrawerBody
        style={{
          marginTop: 0,
        }}
      >
        <Form
          methods={methods}
          onSubmit={onSubmit}
          keyName={`create-otherIncome-${incomableRefType}-${data.refId}`}
          noReset
        >
          <OtherIncomeCard type="create" />
          <Separator gap={16} />
          <FullContainer
            style={{ display: 'flex', justifyContent: 'end', gap: 8 }}
          >
            <Button buttonType="tertiary" onClick={props.onClose}>
              {t('common:cancel')}
            </Button>
            <Input type="submit" />
          </FullContainer>
        </Form>
      </DrawerBody>
    </>
  );
}
